/**
 * Client-side selection state (v0M3.10).
 *
 * Two orthogonal states:
 *  - `selectedArmyId` — persistent army selection. Right-click a map cell
 *    to issue a `move_army` to that army. Cleared by Escape or by
 *    clicking on empty terrain.
 *  - `openCityPanelId` — modal city panel open state (opened by clicking
 *    on an owned city). Closed by Escape or the panel's Close button.
 *
 * Kept as a mutable holder object rather than a state store because the
 * frame loop reads it every tick and the values change on user input,
 * not on sim ticks.
 */

import type { ArmyId, ProvinceId, SpyId, SettlementId, WorldStructureId } from '../sim/state';
import type { BattleId } from '../sim/battle-state';

export interface SelectionState {
  selectedArmyId: ArmyId | null;
  /**
   * v0M5.3 (2026-07-25) — Persistent Spy selection. Mutually exclusive
   * with `selectedArmyId` in practice (single-selection UI at v1). RMB
   * on an empty cell issues `move_spy`; RMB on a settlement (v0M5.4)
   * issues Enter Settlement.
   */
  selectedSpyId: SpyId | null;
  /**
   * Province whose city panel is currently open. Selection uses the
   * province (not the specific city) because a province may have
   * multiple cities but build + recruit commands operate at province
   * scope.
   */
  openPanelProvinceId: ProvinceId | null;
  /**
   * Battle whose battle-command modal is open (v0M3.5). When set, the
   * modal is shown and `enter_battle_command` has been dispatched; when
   * cleared, `exit_battle_command` is dispatched.
   */
  openBattleCommandBattleId: BattleId | null;
  /**
   * v0M9.5 (2026-07-24) — Knowledge modal open flag. Faction-wide, no
   * per-entity state; just a boolean. Toggled by the top-bar Knowledge
   * button or the K hotkey.
   */
  knowledgePanelOpen: boolean;
  /**
   * v0M5.4 (2026-07-25) — Settlement whose infiltration (read-only spy)
   * panel is currently open. Set when the player LMBs a settlement
   * containing one of their own spies. Cleared by Exit / Escape /
   * clicking away.
   */
  openInfiltrationSettlementId: SettlementId | null;
  /**
   * v0M9 (2026-07-27) — Minor settlement whose repair/pillage panel is
   * currently open. Set when the player LMBs one of their own minor
   * settlements. Cleared by Close / Escape / clicking away.
   */
  openMinorSettlementId: SettlementId | null;
  /**
   * v0M7.4d+garrison.2 (2026-07-27) — Fort whose panel is currently
   * open. Set when the player LMBs one of their own Forts. Cleared by
   * Close / Escape / clicking away.
   */
  openFortId: WorldStructureId | null;
  /**
   * MFix12.5b (2026-07-27) — Army whose cohort-transfer modal is open.
   * Set when the player clicks the "Transfer Cohort" button on the Army
   * Info panel. The modal picks target armies internally (any own-faction
   * army within zoc_radius/2 of this source). Cleared by Close / Escape /
   * clicking away.
   */
  openTransferSourceArmyId: ArmyId | null;
}

export function createSelectionState(): SelectionState {
  return {
    selectedArmyId: null,
    selectedSpyId: null,
    openPanelProvinceId: null,
    openBattleCommandBattleId: null,
    knowledgePanelOpen: false,
    openInfiltrationSettlementId: null,
    openMinorSettlementId: null,
    openFortId: null,
    openTransferSourceArmyId: null,
  };
}

export function openTransferModal(state: SelectionState, id: ArmyId): void {
  state.openTransferSourceArmyId = id;
}

export function closeTransferModal(state: SelectionState): void {
  state.openTransferSourceArmyId = null;
}

/** v0M5.4 — Open the infiltration (read-only spy) panel for a settlement. */
export function openInfiltrationPanel(state: SelectionState, id: SettlementId): void {
  state.openInfiltrationSettlementId = id;
  // Close conflicting panels — single-panel UI at v1.
  state.openPanelProvinceId = null;
}

export function closeInfiltrationPanel(state: SelectionState): void {
  state.openInfiltrationSettlementId = null;
}

/** v0M9 — Open the minor settlement pillage/repair panel. */
export function openMinorSettlementPanel(state: SelectionState, id: SettlementId): void {
  state.openMinorSettlementId = id;
  // Close conflicting panels — single-panel UI at v1.
  state.openPanelProvinceId = null;
  state.openInfiltrationSettlementId = null;
  state.openFortId = null;
}

export function closeMinorSettlementPanel(state: SelectionState): void {
  state.openMinorSettlementId = null;
}

/** v0M7.4d+garrison.2 — Open the Fort recruit panel. */
export function openFortPanel(state: SelectionState, id: WorldStructureId): void {
  state.openFortId = id;
  state.openPanelProvinceId = null;
  state.openInfiltrationSettlementId = null;
  state.openMinorSettlementId = null;
}

export function closeFortPanel(state: SelectionState): void {
  state.openFortId = null;
}

export function selectArmy(state: SelectionState, id: ArmyId): void {
  state.selectedArmyId = id;
  state.selectedSpyId = null; // Single-selection UI at v1.
}

export function clearArmySelection(state: SelectionState): void {
  state.selectedArmyId = null;
}

/** v0M5.3 — Select a spy (clears army selection to keep single-selection UI). */
export function selectSpy(state: SelectionState, id: SpyId): void {
  state.selectedSpyId = id;
  state.selectedArmyId = null;
}

export function clearSpySelection(state: SelectionState): void {
  state.selectedSpyId = null;
}

export function openCityPanel(state: SelectionState, id: ProvinceId): void {
  state.openPanelProvinceId = id;
}

export function closeCityPanel(state: SelectionState): void {
  state.openPanelProvinceId = null;
}

export function openBattleCommand(state: SelectionState, id: BattleId): void {
  state.openBattleCommandBattleId = id;
}

export function closeBattleCommand(state: SelectionState): void {
  state.openBattleCommandBattleId = null;
}

export function openKnowledgePanel(state: SelectionState): void {
  state.knowledgePanelOpen = true;
}

export function closeKnowledgePanel(state: SelectionState): void {
  state.knowledgePanelOpen = false;
}

/** Clear everything — used by Escape key. */
export function clearAll(state: SelectionState): void {
  state.selectedArmyId = null;
  state.selectedSpyId = null;
  state.openPanelProvinceId = null;
  state.openBattleCommandBattleId = null;
  state.knowledgePanelOpen = false;
  state.openInfiltrationSettlementId = null;
  state.openMinorSettlementId = null;
  state.openFortId = null;
  state.openTransferSourceArmyId = null;
}
